import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { useCallback, useState } from 'react';

import { TypewriterOnce } from '../components/TypewriterOnce';

import type { StrategyGoal } from './strategySummary';

type StrategyPickScreenProps = {
	// Re-seeds the selection when the user navigates back here: the wizard
	// remounts screens on every transition, so local state doesn't survive.
	initialGoal?: StrategyGoal | null;
	onContinue: (strategy: StrategyGoal) => void;
};

const STRATEGIES: Array<{ id: StrategyGoal; label: string; rest: string }> = [
	{
		id: 'grow_awareness',
		label: 'Grow Awareness',
		rest: 'get more of the right people to know me',
	},
	{
		id: 'generate_leads',
		label: 'Generate leads',
		rest: 'turn existing attention into new customers',
	},
	{
		id: 'nurture_customers',
		label: 'Nurture customers',
		rest: 'keep my existing customers coming back',
	},
];

export const StrategyPickScreen = ({
	initialGoal,
	onContinue,
}: StrategyPickScreenProps) => {
	const [typingDone, setTypingDone] = useState(false);
	const [selected, setSelected] = useState<StrategyGoal | null>(
		initialGoal ?? null,
	);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);
	const handleSubmit = useCallback(() => {
		if (selected) onContinue(selected);
	}, [selected, onContinue]);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="w-full max-w-[760px] px-8">
				<h2
					className="mb-6"
					style={{
						fontSize: 'clamp(26px, 4vw, 40px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
					}}
				>
					<TypewriterOnce
						text="Now to launch a campaign, we need to pick a strategy."
						onDone={handleTypingDone}
					/>
				</h2>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<p
						className="mb-8"
						style={{
							fontSize: 18,
							lineHeight: 1.5,
							color: 'var(--text-primary)',
						}}
					>
						I suggest starting with{' '}
						<strong style={{ fontWeight: 700 }}>Grow Awareness</strong>. But you
						know your business — pick what feels right.
					</p>

					<div
						className="mb-4"
						style={{
							fontSize: 11,
							fontWeight: 700,
							letterSpacing: '0.12em',
							color: '#6B7280',
						}}
					>
						PICK ONE
					</div>

					<div className="mb-8 flex flex-col" style={{ gap: 12 }}>
						{STRATEGIES.map((s) => {
							const isSelected = selected === s.id;
							return (
								<button
									key={s.id}
									type="button"
									role="radio"
									aria-checked={isSelected}
									onClick={() => setSelected(s.id)}
									className="flex w-full items-center text-left transition-all"
									style={{
										gap: 14,
										background: 'white',
										border: isSelected
											? '1.5px solid #1A1A1A'
											: '1.5px solid #E5E7EB',
										borderRadius: 14,
										padding: '16px 20px',
										cursor: 'pointer',
										boxShadow: isSelected
											? '0 0 0 3px rgba(0,0,0,0.04)'
											: '0 1px 2px rgba(0,0,0,0.02)',
									}}
								>
									<span
										aria-hidden="true"
										className="flex shrink-0 items-center justify-center"
										style={{
											width: 20,
											height: 20,
											borderRadius: '50%',
											border: isSelected
												? '2px solid #1A1A1A'
												: '2px solid #C8CCD2',
											background: 'white',
										}}
									>
										{isSelected && (
											<span
												style={{
													width: 10,
													height: 10,
													borderRadius: '50%',
													background: '#1A1A1A',
												}}
											/>
										)}
									</span>
									<span
										style={{
											fontSize: 16,
											lineHeight: 1.4,
											color: 'var(--text-primary)',
										}}
									>
										<strong style={{ fontWeight: 700 }}>{s.label}</strong>
										{' — '}
										{s.rest}
									</span>
								</button>
							);
						})}
					</div>

					<Button
						variant="primary"
						staticColor="black"
						fillStyle="fill"
						size="L"
						isDisabled={!selected}
						onPress={handleSubmit}
					>
						<Text>That&apos;s it</Text>
						<ChevronRight />
					</Button>
				</div>
			</div>
		</div>
	);
};
